import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useParams } from 'react-router-dom';
// API service kaldırıldı - direkt Supabase kullanılıyor
import { User, Mail, Save, MessageSquare, Calendar, Sparkles, Smartphone, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';
import { apiFetch } from '../lib/api';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { isExpanded } = useSidebar();
  const { userId } = useParams(); // URL'den userId parametresini al
  const [loading, setLoading] = useState(false);
  const [userApps, setUserApps] = useState([]);
  const [profile, setProfile] = useState(null);
  
  // Hangi kullanıcının profilini göstereceğimizi belirle
  const targetUserId = userId || user?.id;
  const isOwnProfile = !userId || userId === user?.id;
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: user?.email || ''
    }
  });

  // Profile verisi geldiğinde form'u güncelle
  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        email: profile.email || user?.email || ''
      });
    }
  }, [profile, reset, user?.email]);

  useEffect(() => {
    // Supabase'den gerçek verileri çek
    const fetchProfile = async () => {
      try {
        if (!targetUserId) return;

        const response = await apiFetch(`/api/profiles?id=eq.${targetUserId}&select=*`);
        const data = await response.json();

        if (!response.ok || !data || data.length === 0) {
          logger.error('Profil çekilirken hata');
          // Profil bulunamazsa veya erişim yoksa
          if (targetUserId !== user?.id) {
            // Başka kullanıcının profiline erişim yok
            setProfile(null);
            return;
          }
          // Eğer profil bulunamazsa, mevcut kullanıcının bilgilerini kullan
          if (targetUserId === user?.id) {
            setProfile({
              id: user.id,
              first_name: user.user_metadata?.first_name || '',
              last_name: user.user_metadata?.last_name || '',
              email: user.email
            });
          }
          return;
        }

        setProfile(data[0]);
      } catch (error) {
        logger.error('Profil çekilirken hata:', error);
      }
    };

    const fetchUserApps = async () => {
      try {
        if (!targetUserId) return;

        const response = await apiFetch(`/api/apps?select=*&user_id=eq.${targetUserId}&order=created_at.desc`);
        const data = await response.json();

        if (!response.ok) {
          logger.error('Kullanıcı uygulamaları çekilirken hata');
          return;
        }

        setUserApps(data || []);
      } catch (error) {
        logger.error('Kullanıcı uygulamaları çekilirken hata:', error);
      }
    };

    fetchProfile();
    fetchUserApps();
  }, [targetUserId]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const result = await updateProfile(data);
      if (result.success) {
        toast.success('Profil başarıyla güncellendi');
        // Profil verilerini yeniden çek
        const response = await apiFetch(`/api/profiles?id=eq.${targetUserId}&select=*`);
        const data = await response.json();
        
        if (response.ok && data && data.length > 0) {
          setProfile(data[0]);
        }
      } else {
        toast.error(result.message || 'Profil güncellenirken bir hata oluştu');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = (userId) => {
    // Messages sayfasına yönlendir ve localStorage'da userId sakla
    localStorage.setItem('selectedUserId', userId);
    window.location.href = '/messages';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Mobile Header Gradient */}
      <div className="md:hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Desktop Header Gradient */}
      <div className="hidden md:block bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className={`relative -mt-32 pb-8 transition-all duration-300 ${
        isExpanded ? 'md:ml-72' : 'md:ml-18'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="backdrop-blur-xl bg-white/20 rounded-2xl p-6 shadow-xl border border-white/30">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Profil</h1>
                  <p className="text-white/80">
                    {isOwnProfile ? 'Profil bilgilerinizi düzenleyin' : 'Kullanıcı profili'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* User Profile Card */}
          <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg mb-8 border border-white/40 overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <User className="h-10 w-10 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                      {profile?.first_name} {profile?.last_name}
                    </h3>
                    <p className="text-gray-600 flex items-center mt-1 truncate text-sm">
                      <Mail className="h-4 w-4 mr-2 text-emerald-600" />
                      <span className="truncate max-w-[220px] sm:max-w-none">{profile?.email || user?.email}</span>
                    </p>
                    <p className="text-sm text-gray-500 flex items-center mt-2">
                      <Calendar className="h-4 w-4 mr-2 text-teal-600" />
                      Üyelik Tarihi: {new Date().toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>
                {!isOwnProfile && (
                  <button
                    onClick={() => handleMessage(targetUserId)}
                    className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    <MessageSquare className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                    Mesajlaş
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Profile Edit Form - Sadece kendi profilinde göster */}
          {isOwnProfile && (
          <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg mb-8 border border-white/40">
            <div className="px-8 py-6 border-b border-white/30">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Profil Bilgilerini Düzenle</h2>
              </div>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Ad
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('firstName', {
                        required: 'Ad zorunludur',
                        minLength: {
                          value: 2,
                          message: 'Ad en az 2 karakter olmalıdır'
                        }
                      })}
                      type="text"
                      className="w-full pl-12 pr-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                      placeholder="Adınız"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-2 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Soyad
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...register('lastName', {
                        required: 'Soyad zorunludur',
                        minLength: {
                          value: 2,
                          message: 'Soyad en az 2 karakter olmalıdır'
                        }
                      })}
                      type="text"
                      className="w-full pl-12 pr-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                      placeholder="Soyadınız"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-2 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  E-posta adresi
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('email', {
                      required: 'E-posta adresi zorunludur',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Geçerli bir e-posta adresi giriniz'
                      }
                    })}
                    type="email"
                    className="w-full pl-12 pr-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white/80 transition-all duration-300"
                    placeholder="E-posta adresiniz"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <Save className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
          )}

          {/* User Applications */}
          <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg border border-white/40">
            <div className="px-8 py-6 border-b border-white/30">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                  <Smartphone className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  {isOwnProfile ? 'Uygulamalarım' : `${profile?.first_name || 'Kullanıcının'} Uygulamaları`}
                </h2>
              </div>
            </div>
            
            <div className="p-8">
              {userApps.length === 0 ? (
                <div className="text-center py-12">
                  <Smartphone className="mx-auto h-16 w-16 text-emerald-300" />
                  <p className="text-gray-600 mt-4">Henüz uygulama eklenmemiş</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {userApps.map((app) => (
                    <div key={app.id} className="backdrop-blur-sm bg-white/40 rounded-xl p-6 border border-white/30 hover:shadow-lg transition-all duration-300 group">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors">
                            {app.title}
                          </h3>
                          <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>
                          <div className="flex items-center space-x-6 text-sm text-gray-600">
                            <div className="flex items-center">
                              <span className="font-medium text-emerald-700">Platform:</span>
                              <span className="ml-2 px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs">
                                {app.platform}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Users className="h-4 w-4 mr-1 text-teal-600" />
                              <span className="font-medium">{app.current_testers || 0}/{app.max_testers || 12}</span>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center text-xs text-gray-500">
                            <Calendar className="h-3 w-3 mr-1 text-cyan-600" />
                            Oluşturulma: {new Date(app.created_at).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                        <div className="ml-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            app.status === 'active' 
                              ? 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200' 
                              : 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200'
                          }`}>
                            {app.status === 'active' ? 'Aktif' : app.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;